import type TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import getRowKeysFromRowIds from 'owa-mail-list-store/lib/utils/getRowKeysFromRowIds';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { getActiveSecondaryReadingPaneTabItemId } from 'owa-tab-store';

/**
 * Returns the effective rowKeys for a triage action.
 *
 * When a secondary reading pane tab is active, resolves its item/conversation
 * ID to row keys so the action targets the item in that tab rather than the
 * currently selected row in the list view.  Otherwise falls back to the
 * table's selectedRowKeys (normal main-window behaviour).
 */
export default function getEffectiveRowKeys(tableView: TableView): string[] {
    if (!isFeatureEnabled('mon-ribbon-secondary-rp-activation')) {
        return [...tableView.selectedRowKeys.keys()];
    }
    const secondaryTabItemId = getActiveSecondaryReadingPaneTabItemId();
    if (secondaryTabItemId) {
        const rowKeys = resolveRowKeysFromTabItemId(secondaryTabItemId, tableView);
        if (rowKeys.length > 0) {
            return rowKeys;
        }
    }
    return [...tableView.selectedRowKeys.keys()];
}

/**
 * Resolves the tab's item/conversation ID to row keys.
 *
 * The rowIdToRowKeyMap may be keyed by ConversationId or ItemId depending
 * on the list view type.  The secondary tab stores an ID that may be
 * either kind, so we first try the item→ConversationId path.  If that
 * fails we fall back to a direct lookup.
 */
function resolveRowKeysFromTabItemId(itemId: string, tableView: TableView): string[] {
    // Check if the ID is an ItemId — if so, resolve its ConversationId for the map lookup
    const item = getMailStore().items.get(itemId);
    const conversationId = item?.ConversationId?.Id;
    if (conversationId) {
        const rowKeys = getRowKeysFromRowIds([conversationId], tableView);
        if (rowKeys.length > 0) {
            return rowKeys;
        }
    }

    // Fall back to direct lookup (works in message view or stacked conversation
    // view where the tab already holds an ID that matches the map key)
    return getRowKeysFromRowIds([itemId], tableView);
}
